/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactElement, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage, RequiredAsterisk } from '@atlaskit/form';
import Select, {
	type OptionsType,
	type FormatOptionLabelMeta,
	components,
	type MenuListComponentProps,
	type SelectComponentsConfig,
	CheckboxOption,
	type ValueType,
	type ActionMeta,
} from '@atlaskit/select';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { B50, B100, B300, N0, N30, N40, N200, N900 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { messages } from '../i18n';
import { getProductIcon, JiraSoftwareProduct, JiraWorkManagementProduct } from '../../utils';
import { type ProductSelectOption, ZIndex } from '../../types';
import { useProducts } from './ProductSelectContext';
import {
	mapProductIdsToProductSelectOptions,
	removeJiraCoreIfImplied,
	hasNonCoreJira,
} from './ProductSelectUtils';
import { useAnalytics } from '../../analytics';
import { ProductSelectAnalytics } from './ProductSelectAnalytics';

const MAX_VISIBLE_PRODUCTS = 2;

const styles = cssMap({
	fieldWrapper: {
		position: 'relative',
	},
	iconWrapper: {
		width: '28px',
		height: '28px',
		borderRadius: token('border.radius', '3px'),
		marginLeft: token('space.050', '4px'),
		marginRight: token('space.100', '8px'),
		paddingBlock: token('space.025', '2px'),
		paddingInline: token('space.025', '2px'),
	},
	labelWrapper: {
		flex: '1 1 auto',
	},
	label: {
		display: 'block',
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		color: token('color.text.subtlest', '#626F86'),
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.050', '4px'),
	},
	productValues: {
		flex: '1 1 auto',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	chooseAllButtonWrapper: {
		paddingBlock: token('space.100', '8px'),
		paddingInlineStart: token('space.250', '20px'),
		paddingInlineEnd: token('space.150', '12px'),
	},
});

type ProductOption = ProductSelectOption;

export interface OwnProps {
	onMenuOpen: () => void;
	productOptions?: ProductSelectOption[];
	defaultProductOptions?: ProductOption[];
	formInvalid?: boolean;
}

const isJiraCoreImplied = (option: ProductOption, selectedOptions: OptionsType<ProductOption>) =>
	option.value === JiraWorkManagementProduct.id &&
	hasNonCoreJira(selectedOptions.map((option) => option.value));

const customOptionLabel = (
	option: ProductOption,
	{ context, selectValue }: FormatOptionLabelMeta<ProductOption>,
) => {
	let LabelIcon = getProductIcon(option.value);

	// If the label is Jira Work Management (due to JSW and JWM is shown in same place)
	// then it should render original JWM icon instead of the rebranded Jira icon.
	if (option.label === JiraWorkManagementProduct.label) {
		LabelIcon = JiraWorkManagementProduct.icon;
	}

	if (context === 'menu') {
		const isImpliedJiraCore = isJiraCoreImplied(option, selectValue);

		return (
			<Tooltip
				content={
					isImpliedJiraCore ? (
						<FormattedMessage {...messages.optionIncludedWithOtherJiraProducts} />
					) : undefined
				}
				position={'right'}
			>
				<Inline testId={`test-id-option-${option.value}`} alignBlock="center">
					<Box backgroundColor="color.background.neutral" xcss={styles.iconWrapper}>
						<LabelIcon size="small" appearance="brand" />
					</Box>
					<Inline xcss={styles.labelWrapper}>{option.label}</Inline>
					{isImpliedJiraCore && (
						<EditorPanelIcon label={'EditorPanelIcon'} color={token('color.icon.subtle', N200)} />
					)}
				</Inline>
			</Tooltip>
		);
	}
	return option.label;
};

const ProductSelect: React.FC<OwnProps> = ({ onMenuOpen, formInvalid }) => {
	const analytics = useAnalytics(ProductSelectAnalytics);
	const { selectedProducts, setSelectedProducts, availableProductOptions } = useProducts();

	const [isInvalid, setIsInvalid] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState(false);

	useEffect(() => {
		analytics.productSelectRendered(availableProductOptions);
	}, [availableProductOptions, analytics]);

	useEffect(() => {
		if (formInvalid && (!selectedProducts || !selectedProducts.length)) {
			setIsInvalid(formInvalid);
		}
	}, [formInvalid, selectedProducts]);

	// Disable JWM if another jira product is selected
	const isOptionDisabled = React.useCallback(
		(option: ProductOption) => {
			return option.value === JiraWorkManagementProduct.id && hasNonCoreJira(selectedProducts);
		},
		[selectedProducts],
	);
	const isOptionSelected = (option: ProductOption, selectedOptions: OptionsType<ProductOption>) => {
		return (
			selectedOptions.some((currentOption) => option.value === currentOption.value) ||
			isJiraCoreImplied(option, selectedOptions)
		);
	};

	const handleOnChangeProduct = React.useCallback(
		(values: ValueType<ProductOption, true>, _action: ActionMeta<ProductOption>) => {
			if (!values) {
				return;
			}

			const selectedOptions = [...values];

			setSelectedProducts(removeJiraCoreIfImplied(selectedOptions.map((option) => option.value)));
			setIsInvalid(!(selectedOptions && selectedOptions.length));
		},
		[setSelectedProducts],
	);

	const customMenuListFactory =
		({
			onSelectAll = (_allProducts: ProductOption[]) => {},
		}): React.FC<MenuListComponentProps<ProductOption, true>> =>
		(props) => {
			const onClickSelectAll = () => {
				const allOptions = [...props.options] as ProductOption[];
				props.setValue(allOptions, 'select-option');
				onSelectAll(allOptions);
			};

			return (
				<components.MenuList {...props}>
					<Box xcss={styles.chooseAllButtonWrapper}>
						<Button
							onMouseDown={onClickSelectAll}
							appearance="link"
							spacing="none"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ fontWeight: token('font.weight.regular') }}
						>
							<FormattedMessage {...messages.selectAllOption} />
						</Button>
					</Box>
					{props.children}
				</components.MenuList>
			);
		};

	const handleMenuOpen = React.useCallback(() => {
		setIsOpen(true);
		if (onMenuOpen) {
			onMenuOpen();
		}
	}, [onMenuOpen]);

	const handleMenuClose = () => setIsOpen(false);

	const customComponents = React.useMemo<
		SelectComponentsConfig<ProductOption | ProductOption, true>
	>(
		() => ({
			MenuList: customMenuListFactory({
				onSelectAll: (selectedProducts) => {
					analytics.selectAllClicked(selectedProducts.map((p) => String(p.value)));
					setIsOpen(false);
				},
			}),

			MultiValue: (props) => {
				const selectValue = (props.selectProps.value as []) || [];

				// `index` *is* passed by react-select, despite not being in MultiValueProps
				const { data, index } = props as typeof props & { index: number };

				const isLast = index === selectValue.length - 1;

				let suffix = ',';
				if (isLast) {
					suffix = '';
				} else if (index >= 1) {
					suffix = ', ...';
				}

				return (
					<span>
						{data.label}
						{suffix}&nbsp;
					</span>
				);
			},

			Option: (props) => {
				return <CheckboxOption {...props} />;
			},

			ValueContainer: ({ children, ...props }) => {
				const selectedOptions = (children as [ReactElement[], ReactElement])[0];
				return (
					<components.ValueContainer {...props}>
						<Inline as="span" xcss={styles.productValues}>
							{children}
						</Inline>
						<span>
							{props.hasValue &&
								selectedOptions.length > MAX_VISIBLE_PRODUCTS &&
								`(+${selectedOptions.length - MAX_VISIBLE_PRODUCTS})`}
						</span>
					</components.ValueContainer>
				);
			},
		}),
		[analytics],
	);

	const customStyles = {
		// main area
		control: (provided: any, state: any) => ({
			...provided,
			cursor: 'pointer',
			background: state.selectProps.menuIsOpen
				? token('color.background.selected', B50)
				: state.isFocused
					? token('color.background.neutral', N30)
					: token('color.background.input', N0),
			height: '48px',
			paddingLeft: token('space.150', '12px'),
			borderRadius: '3px',
			borderColor: state.isFocused
				? state.selectProps.menuIsOpen
					? 'transparent'
					: token('color.border.focused', B100)
				: token('color.border', N40),
			':hover': {
				background: state.selectProps.menuIsOpen
					? token('color.background.selected', B50)
					: token('color.background.neutral.hovered', N40),
				borderColor: state.isFocused
					? state.selectProps.menuIsOpen
						? 'transparent'
						: token('color.border.focused', B100)
					: 'transparent',
			},
		}),
		valueContainer: (provided: any, state: any) => ({
			...provided,
			color: state.selectProps.menuIsOpen
				? token('color.text.brand', B300)
				: token('color.text', N900),
			display: 'flex',
			flexWrap: 'nowrap',
			minWidth: 0,
			[`> span > span:nth-child(n + ${MAX_VISIBLE_PRODUCTS + 1})`]: {
				display: 'none',
			},
		}),
		placeholder: (provided: any, state: any) => ({
			...provided,
			color: state.selectProps.menuIsOpen
				? token('color.text.brand', B300)
				: token('color.text', N900),
		}),
		dropdownIndicator: (provided: any, state: any) => ({
			...provided,
			color: state.selectProps.menuIsOpen
				? token('color.text.brand', B300)
				: token('color.text', N900),
			':hover': 'none',
		}),
		menuPortal: (provided: any) => ({ ...provided, zIndex: ZIndex.FLOATING }),
	};

	const selectedValue = React.useMemo(() => {
		const selectedOptions = mapProductIdsToProductSelectOptions(selectedProducts);

		const hasBothJSWAndJWM =
			availableProductOptions.find(({ value }) => value === JiraWorkManagementProduct.id) &&
			availableProductOptions.find(({ value }) => value === JiraSoftwareProduct.id);

		// If JSW and JWM are both shown in options,
		// it should show original label of JWM as Jira Work Management instead of rebranded Jira label
		return hasBothJSWAndJWM
			? selectedOptions.map((option) =>
					option.value === JiraWorkManagementProduct.id
						? {
								label: JiraWorkManagementProduct.label,
								value: option.value,
							}
						: option,
				)
			: selectedOptions;
	}, [selectedProducts, availableProductOptions]);

	return (
		<Box testId="test-id-invite-people-select-product" xcss={styles.fieldWrapper}>
			<Box as="label" htmlFor="product-select" xcss={styles.label}>
				{selectedProducts.length === 0 ? (
					<FormattedMessage {...messages.productSelectNoCurrentProduct} />
				) : (
					<FormattedMessage {...messages.productSelect} />
				)}
				<RequiredAsterisk />
			</Box>
			{
				// We have to use ordinary Select because CheckboxSelect
				// does not allow to override the Option component
			}
			<Select<ProductOption, true>
				isMulti
				styles={customStyles}
				isClearable={false}
				isSearchable={false}
				name="product-select"
				inputId="product-select"
				placeholder={<FormattedMessage {...messages.selectPlaceholder} />}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="checkbox-select"
				classNamePrefix="select"
				closeMenuOnSelect={false}
				components={customComponents}
				hideSelectedOptions={false}
				options={availableProductOptions}
				value={selectedValue}
				formatOptionLabel={customOptionLabel}
				onChange={handleOnChangeProduct}
				isOptionDisabled={isOptionDisabled}
				isOptionSelected={isOptionSelected}
				onMenuOpen={handleMenuOpen}
				onMenuClose={handleMenuClose}
				menuIsOpen={isOpen}
				validationState={isInvalid ? 'error' : 'default'}
				menuPortalTarget={document.body}
				menuPosition={'absolute'}
				isRequired={true}
			/>
			{isInvalid ? (
				<ErrorMessage>
					<FormattedMessage {...messages.noProductSelectedMessage} />
				</ErrorMessage>
			) : null}
		</Box>
	);
};

export default ProductSelect;
